/**
 * Artigos do blog: pagamentos e gestão
 */

import type { BlogArticle } from '../types';

const PUBLISHED = '2026-09-27';

export const GESTAO_ARTICLES: BlogArticle[] = [
  {
    slug: 'pix-no-delivery-golpe-do-comprovante',
    title: 'PIX no delivery: como evitar o golpe do comprovante falso',
    description:
      'Como funciona o golpe do comprovante de PIX falso no delivery, sinais de alerta e como a confirmação automática pelo banco elimina o problema.',
    excerpt:
      'Print de PIX não é prova de pagamento. Veja como o golpe funciona e como receber PIX com confirmação automática.',
    category: 'Pagamentos',
    keywords: [
      'golpe comprovante PIX falso',
      'PIX no delivery',
      'confirmar pagamento PIX automático',
      'PIX para restaurante',
      'como receber PIX no delivery',
    ],
    publishedAt: PUBLISHED,
    updatedAt: PUBLISHED,
    blocks: [
      {
        type: 'p',
        text: 'O PIX virou a forma de pagamento preferida no delivery: é rápido e não tem maquininha. Mas junto veio um golpe comum: o cliente envia **um print de comprovante editado ou de um PIX agendado que nunca é concluído**, e o restaurante só descobre ao conferir o extrato, com o pedido já entregue.',
      },
      { type: 'h2', text: 'Sinais de alerta' },
      {
        type: 'ul',
        items: [
          'Comprovante com data, horário ou valor estranho.',
          'PIX "agendado" em vez de concluído.',
          'Nome do recebedor diferente do seu.',
          'Pressa do cliente para você liberar o pedido.',
        ],
      },
      { type: 'h2', text: 'A solução: confirmação pelo banco, não pelo print' },
      {
        type: 'p',
        text: 'A forma segura é gerar uma **cobrança PIX para cada pedido** (QR Code e copia e cola com o valor exato) e deixar o sistema **consultar o banco** para confirmar o pagamento. Quando o dinheiro entra de verdade, o pedido é marcado como pago sozinho. Ninguém precisa abrir o extrato no meio do movimento.',
      },
      {
        type: 'table',
        head: ['PIX por chave + print', 'PIX com cobrança e confirmação automática'],
        rows: [
          ['Alguém confere o extrato na mão', 'O sistema confere com o banco'],
          ['Risco de comprovante falso', 'Só confirma se o dinheiro entrou'],
          ['Valor digitado pelo cliente', 'Valor exato do pedido no QR Code'],
          ['Estorno manual', 'Estorno automático nos provedores que permitem'],
        ],
      },
      { type: 'h2', text: 'Como funciona no Vende Aí' },
      {
        type: 'p',
        text: 'Você conecta o seu banco ou gateway (Mercado Pago, PagBank, Asaas, Banco Inter, entre outros) e cada pedido gera a própria cobrança PIX. O sistema confirma consultando o provedor e o dinheiro cai **direto na sua conta**, sem intermediação. Se o pedido for cancelado depois de pago, a devolução pode ser feita pelo próprio sistema nos provedores com estorno.',
      },
      {
        type: 'cta',
        text: 'Receba PIX com confirmação automática no Vende Aí. Teste grátis por 30 dias.',
      },
    ],
    faq: [
      {
        question: 'Como evitar comprovante de PIX falso no delivery?',
        answer:
          'Não libere o pedido com base em print. Gere uma cobrança PIX por pedido e use um sistema que confirma o pagamento consultando o banco; assim o pedido só é marcado como pago quando o dinheiro entra.',
      },
      {
        question: 'O dinheiro do PIX passa pelo sistema?',
        answer:
          'No Vende Aí, não: o PIX é gerado na conta do seu banco ou gateway e o valor cai direto para você. O sistema só consulta o status para confirmar.',
      },
    ],
  },
  {
    slug: 'ficha-tecnica-e-cmv',
    title: 'Ficha técnica e CMV: como saber o lucro real de cada prato',
    description:
      'O que é ficha técnica e CMV, como calcular o custo de cada prato, qual a faixa saudável de CMV e como o controle de estoque automático ajuda.',
    excerpt:
      'Sem ficha técnica, o preço é chute. Veja como calcular o custo de cada prato e acompanhar o CMV todo mês.',
    category: 'Gestão',
    keywords: [
      'ficha técnica restaurante',
      'como calcular CMV',
      'CMV restaurante',
      'custo do prato',
      'precificação cardápio',
    ],
    publishedAt: PUBLISHED,
    updatedAt: PUBLISHED,
    relatedSegment: 'restaurante',
    blocks: [
      {
        type: 'p',
        text: '**Ficha técnica** é a "receita com custo" de cada prato: a lista dos ingredientes, a quantidade de cada um e quanto isso custa. **CMV** (custo da mercadoria vendida) é a soma do custo dos ingredientes de tudo o que você vendeu no período.',
      },
      { type: 'h2', text: 'Como montar uma ficha técnica' },
      {
        type: 'ol',
        items: [
          'Liste todos os ingredientes, inclusive os pequenos (sal, óleo, embalagem).',
          'Anote a quantidade usada em cada prato (em gramas, mililitros ou unidades).',
          'Use o custo de compra atualizado de cada ingrediente.',
          'Considere a perda (casca, osso, sobra de preparo) quando existir.',
          'Some tudo: esse é o custo do prato.',
        ],
      },
      { type: 'h2', text: 'Como calcular o CMV' },
      {
        type: 'p',
        text: 'A fórmula clássica é: **CMV = estoque inicial + compras do período − estoque final**. O **CMV percentual** é o CMV dividido pelo faturamento do período. Muitos restaurantes trabalham com CMV entre 25% e 35%, mas a faixa ideal depende do tipo de operação: compare com o seu histórico e com o seu contador.',
      },
      {
        type: 'tip',
        title: 'Por que automatizar',
        text: 'Quando cada venda baixa os ingredientes do estoque pela ficha técnica, você acompanha o consumo em tempo real e recebe alerta antes do insumo acabar.',
      },
      {
        type: 'p',
        text: 'No Vende Aí, os produtos podem ter ficha técnica: cada venda baixa os ingredientes do estoque, e o financeiro reúne receitas, despesas e relatórios no mesmo sistema. Veja como isso funciona para [restaurantes](/para/restaurante) e [padarias](/para/padaria).',
      },
      { type: 'cta', text: 'Controle custo e estoque no Vende Aí. Teste grátis por 30 dias.' },
    ],
    faq: [
      {
        question: 'Qual é um bom CMV para restaurante?',
        answer:
          'Muitos restaurantes trabalham com CMV entre 25% e 35% do faturamento, mas a faixa ideal varia com o tipo de operação. Acompanhe o seu histórico mês a mês.',
      },
      {
        question: 'Como calcular o CMV?',
        answer:
          'CMV = estoque inicial + compras do período − estoque final. Para o percentual, divida o CMV pelo faturamento do mesmo período.',
      },
    ],
  },
  {
    slug: 'nota-fiscal-no-delivery',
    title: 'Nota fiscal no delivery: NFC-e ou NF-e? Guia para restaurantes',
    description:
      'Diferença entre NFC-e e NF-e para restaurantes e delivery, o que é preciso para emitir (certificado digital, credenciamento) e como automatizar.',
    excerpt:
      'NFC-e ou NF-e? Entenda a diferença, o que você precisa para emitir e como integrar a emissão às vendas.',
    category: 'Gestão',
    keywords: [
      'nota fiscal delivery',
      'NFC-e restaurante',
      'NF-e ou NFC-e',
      'emissor de nota fiscal para restaurante',
      'certificado digital A1',
    ],
    publishedAt: PUBLISHED,
    updatedAt: PUBLISHED,
    blocks: [
      {
        type: 'p',
        text: 'Emitir nota fiscal é obrigação para a maioria dos negócios de alimentação, e cada estado tem as suas regras. Este guia explica o básico; **confirme os detalhes com o seu contador**.',
      },
      { type: 'h2', text: 'NFC-e x NF-e' },
      {
        type: 'table',
        head: ['', 'NFC-e (modelo 65)', 'NF-e (modelo 55)'],
        rows: [
          ['Para quem', 'Venda ao consumidor final', 'Venda para empresas e operações específicas'],
          [
            'Uso típico',
            'Balcão, salão e delivery para pessoa física',
            'Cliente que pede nota com CNPJ, eventos corporativos',
          ],
          [
            'Documento do cliente',
            'CPF opcional (conforme regras do estado)',
            'Dados completos do destinatário',
          ],
        ],
      },
      { type: 'h2', text: 'O que você precisa para emitir' },
      {
        type: 'ol',
        items: [
          'CNPJ com inscrição estadual (quando exigida).',
          'Credenciamento na SEFAZ do seu estado para emitir NFC-e e/ou NF-e.',
          'Certificado digital A1 do CNPJ (arquivo .pfx).',
          'Código de segurança do contribuinte (CSC), no caso da NFC-e.',
          'Produtos com a tributação correta (NCM, CFOP, CST/CSOSN), definida com o contador.',
        ],
      },
      {
        type: 'tip',
        title: 'Automatize',
        text: 'Quando a nota é emitida a partir do pedido, ninguém redigita itens e valores, e o risco de erro cai muito.',
      },
      {
        type: 'p',
        text: 'No Vende Aí, os planos Completo e Pro emitem NF-e e NFC-e integradas à SEFAZ, usando o certificado digital A1 do seu CNPJ, a partir dos pedidos.',
      },
      { type: 'cta', text: 'Venda e emita nota no mesmo sistema. Teste o Vende Aí por 30 dias.' },
    ],
    faq: [
      {
        question: 'Restaurante emite NFC-e ou NF-e?',
        answer:
          'Para venda ao consumidor final (balcão, salão e delivery para pessoa física), normalmente NFC-e. Para vendas a empresas, NF-e. As regras variam por estado; confirme com o seu contador.',
      },
      {
        question: 'Preciso de certificado digital para emitir NFC-e?',
        answer:
          'Sim. Em geral é usado o certificado digital A1 do CNPJ, além do credenciamento na SEFAZ e do código de segurança do contribuinte (CSC).',
      },
    ],
  },
  {
    slug: 'como-fidelizar-clientes-do-delivery',
    title: 'Como fidelizar clientes do delivery com pontos, cashback e cupons',
    description:
      'Estratégias práticas para fidelizar clientes do delivery: programa de pontos, cashback, cupons, campanhas automáticas e atendimento pelo WhatsApp.',
    excerpt:
      'Trazer um cliente de volta custa menos do que conquistar um novo. Veja como montar um programa de fidelidade simples.',
    category: 'Vendas',
    keywords: [
      'fidelizar clientes delivery',
      'programa de fidelidade restaurante',
      'cashback delivery',
      'cupom de desconto delivery',
      'marketing para restaurante',
    ],
    publishedAt: PUBLISHED,
    updatedAt: PUBLISHED,
    blocks: [
      {
        type: 'p',
        text: 'No delivery, boa parte do faturamento vem de quem já comprou antes. Fidelizar é dar ao cliente **um motivo concreto para pedir de novo, direto com você**.',
      },
      { type: 'h2', text: 'Modelos que funcionam' },
      {
        type: 'table',
        head: ['Modelo', 'Como funciona', 'Bom para'],
        rows: [
          [
            'Pontos',
            'A cada R$ X gastos, o cliente ganha pontos e troca por recompensas',
            'Pedidos frequentes',
          ],
          [
            'Cashback',
            'Parte do valor volta como crédito para o próximo pedido',
            'Aumentar a recompra',
          ],
          ['Cupom', 'Desconto com regra e validade', 'Primeira compra e datas especiais'],
          [
            'Brinde ou frete grátis',
            'Recompensa sem mexer no preço do produto',
            'Proteger a margem',
          ],
        ],
      },
      { type: 'h2', text: 'Campanhas automáticas que valem a pena' },
      {
        type: 'ul',
        items: [
          '**Boas-vindas** depois da primeira compra.',
          '**Aniversário** do cliente.',
          '**Reativação** de quem não pede há algumas semanas.',
          '**Pós-venda** pedindo avaliação.',
          '**Promoção relâmpago** em dias de movimento fraco.',
        ],
      },
      {
        type: 'tip',
        title: 'Regra de ouro',
        text: 'Comece simples: uma recompensa fácil de entender vale mais do que um regulamento complicado.',
      },
      {
        type: 'p',
        text: 'No Vende Aí você configura pontos, cashback, recompensas (inclusive produto grátis e frete grátis), cupons e campanhas automáticas de aniversário, boas-vindas, pós-venda e reativação. O cliente acompanha os pontos no portal da loja.',
      },
      { type: 'cta', text: 'Monte seu programa de fidelidade no Vende Aí, com 30 dias grátis.' },
    ],
    faq: [
      {
        question: 'Qual o melhor programa de fidelidade para delivery?',
        answer:
          'O mais simples de entender. Pontos ou cashback com recompensas claras, somados a campanhas automáticas de aniversário e reativação, costumam dar bom resultado.',
      },
    ],
  },
  {
    slug: 'como-calcular-preco-do-cardapio',
    title: 'Como calcular o preço do cardápio: markup, margem e exemplos práticos',
    description:
      'Aprenda a calcular o preço de venda dos pratos com CMV, markup e margem, incluindo taxas de entrega, embalagem e comissão de marketplace.',
    excerpt:
      'Preço chutado é lucro perdido. Veja a conta passo a passo, com exemplo de pizza e de hambúrguer, e como ajustar o preço para cada canal de venda.',
    category: 'Gestão',
    keywords: [
      'como calcular preço do cardápio',
      'markup restaurante',
      'preço de venda de comida',
      'margem de lucro restaurante',
      'como precificar delivery',
    ],
    publishedAt: PUBLISHED,
    updatedAt: PUBLISHED,
    relatedSegment: 'restaurante',
    blocks: [
      {
        type: 'p',
        text: 'Para calcular o preço de um prato, some o **custo dos ingredientes** (o CMV do prato), acrescente os **custos variáveis** da venda (embalagem, taxa do cartão, comissão) e aplique um **markup** que cubra as despesas fixas e deixe lucro. Parece complicado, mas é uma conta de três passos.',
      },
      { type: 'h2', text: 'Passo 1: descubra o custo do prato (CMV)' },
      {
        type: 'p',
        text: 'O CMV (custo da mercadoria vendida) do prato é a soma do custo de cada ingrediente, na quantidade usada. É para isso que serve a [ficha técnica](/blog/ficha-tecnica-e-cmv): ela lista os ingredientes, as quantidades e o custo de cada um.',
      },
      {
        type: 'table',
        head: ['Ingrediente (pizza grande de mussarela)', 'Quantidade', 'Custo'],
        rows: [
          ['Massa', '1 disco', 'R$ 2,50'],
          ['Molho de tomate', '120 g', 'R$ 1,20'],
          ['Mussarela', '350 g', 'R$ 12,25'],
          ['Orégano e azeitona', 'a gosto', 'R$ 0,80'],
          ['**Total (CMV do prato)**', '', '**R$ 16,75**'],
        ],
      },
      {
        type: 'tip',
        title: 'Atenção',
        text: 'Os valores são um exemplo. Use o preço que você paga hoje aos seus fornecedores e atualize a ficha sempre que um insumo subir.',
      },
      { type: 'h2', text: 'Passo 2: some os custos variáveis da venda' },
      {
        type: 'ul',
        items: [
          '**Embalagem**: caixa, sacola, guardanapo, sachês.',
          '**Taxa do pagamento**: PIX costuma ter taxa menor que cartão; confira as do seu banco ou gateway.',
          '**Comissão do canal**: no marketplace, pode passar de 20% do pedido. No canal próprio com mensalidade fixa, não há comissão por pedido.',
        ],
      },
      { type: 'h2', text: 'Passo 3: aplique o markup' },
      {
        type: 'p',
        text: 'O **markup** é o número pelo qual você multiplica o custo para chegar ao preço. Ele precisa cobrir as **despesas fixas** (aluguel, salários, energia), os **impostos** e o **lucro** que você quer. A fórmula mais usada é:',
      },
      {
        type: 'p',
        text: '**Markup = 100 ÷ (100 − despesas fixas % − impostos % − lucro desejado %)**',
      },
      {
        type: 'p',
        text: 'Exemplo: despesas fixas de 30% do faturamento, impostos de 6% e lucro desejado de 12%. Markup = 100 ÷ (100 − 30 − 6 − 12) = 100 ÷ 52 = **1,92**.',
      },
      {
        type: 'table',
        head: ['Item', 'Valor'],
        rows: [
          ['CMV da pizza', 'R$ 16,75'],
          ['Embalagem', 'R$ 2,50'],
          ['Custo direto', 'R$ 19,25'],
          ['Markup', '1,92'],
          [
            '**Preço no canal próprio**',
            '**R$ 19,25 × 1,92 ≈ R$ 37,00 (arredonde para R$ 36,90 ou R$ 37,90)**',
          ],
        ],
      },
      { type: 'h2', text: 'E o preço no marketplace?' },
      {
        type: 'p',
        text: 'Se o marketplace cobra 20% sobre o pedido, o mesmo cálculo com a comissão no denominador fica: 100 ÷ (100 − 30 − 6 − 12 − 20) = 100 ÷ 32 = **3,13**. Aplicado ao custo direto de R$ 19,25, o preço vai para cerca de **R$ 60**. Na prática, muita gente não repassa tudo e aceita um lucro menor no aplicativo, por isso vale levar o cliente fiel para o canal próprio. Veja [quanto a comissão pesa no lucro](/blog/comissao-do-ifood-quanto-custa).',
      },
      { type: 'h2', text: 'Margem x markup: não confunda' },
      {
        type: 'ul',
        items: [
          '**Markup** é o multiplicador sobre o custo (ex.: 1,92).',
          '**Margem** é quanto do preço sobra depois de pagar o custo (ex.: (36,90 − 19,25) ÷ 36,90 = 48% de margem de contribuição).',
          'O lucro de verdade só aparece depois de pagar as despesas fixas. Acompanhe no financeiro todo mês.',
        ],
      },
      { type: 'h2', text: 'Erros comuns de precificação' },
      {
        type: 'ol',
        items: [
          'Copiar o preço do concorrente sem saber o próprio custo.',
          'Esquecer a embalagem e a taxa do cartão.',
          'Usar o mesmo preço em todos os canais, pagando comissão em alguns deles.',
          'Não revisar os preços quando os insumos sobem.',
          'Dar desconto sem calcular se ainda sobra margem.',
        ],
      },
      {
        type: 'p',
        text: 'No Vende Aí, os produtos podem ter ficha técnica que baixa os ingredientes do estoque a cada venda, e o financeiro reúne receitas, despesas e relatórios, o que ajuda a revisar os preços com números reais.',
      },
      {
        type: 'cta',
        text: 'Organize custos, estoque e vendas no Vende Aí. Teste grátis por 30 dias.',
        segment: 'RESTAURANT',
      },
    ],
    faq: [
      {
        question: 'Qual o markup ideal para restaurante?',
        answer:
          'Não existe um número único: depende das suas despesas fixas, impostos e do lucro desejado. Muitos restaurantes ficam entre 2 e 3 sobre o custo direto, mas calcule com os seus números usando a fórmula 100 ÷ (100 − despesas % − impostos % − lucro %).',
      },
      {
        question: 'O preço no iFood deve ser igual ao da loja própria?',
        answer:
          'Não precisa. Muitos estabelecimentos cobram mais no marketplace para cobrir a comissão e mantêm o preço menor no canal próprio. Confira as regras do seu contrato com a plataforma.',
      },
      {
        question: 'De quanto em quanto tempo devo revisar os preços?',
        answer:
          'Revise a ficha técnica sempre que um insumo importante subir e faça uma revisão geral dos preços pelo menos a cada três meses.',
      },
    ],
  },
  {
    slug: 'pagamento-na-entrega-ou-online',
    title: 'Pagamento na entrega ou online no delivery: prós, contras e como decidir',
    description:
      'Compare pagamento na entrega (dinheiro, maquininha) e pagamento online (PIX e cartão) no delivery: riscos, taxas, troco e como oferecer os dois com segurança.',
    excerpt:
      'Troco, maquininha sem sinal, golpe do comprovante, pedido não pago... Veja quando vale cobrar na entrega, quando vale cobrar online e como fazer os dois bem.',
    category: 'Pagamentos',
    keywords: [
      'pagamento na entrega delivery',
      'pagamento online delivery',
      'PIX no delivery',
      'maquininha no delivery',
      'cartão online restaurante',
    ],
    publishedAt: PUBLISHED,
    updatedAt: PUBLISHED,
    blocks: [
      {
        type: 'p',
        text: 'A resposta curta: **ofereça os dois**, mas incentive o pagamento online. O pagamento na entrega ainda é importante para muitos clientes, enquanto o online reduz troco, filas na maquininha e pedidos que nunca são pagos. O segredo é configurar cada forma de pagamento para não abrir brecha para prejuízo.',
      },
      { type: 'h2', text: 'Comparação rápida' },
      {
        type: 'table',
        head: ['', 'Na entrega (dinheiro ou maquininha)', 'Online (PIX ou cartão no pedido)'],
        rows: [
          ['Quando o dinheiro é garantido', 'Só na entrega', 'Antes de preparar'],
          ['Troco', 'Precisa levar', 'Não existe'],
          ['Risco de calote ou trote', 'Maior', 'Menor'],
          ['Tempo do entregador na porta', 'Maior', 'Menor'],
          ['Preferência de parte dos clientes', 'Alta', 'Crescente'],
          ['Taxas', 'Da maquininha', 'Do banco ou gateway'],
        ],
      },
      { type: 'h2', text: 'Pagamento na entrega: como reduzir os problemas' },
      {
        type: 'ul',
        items: [
          '**Pergunte o troco no pedido**: o cliente informa para quanto precisa de troco e o entregador sai preparado.',
          '**Maquininha carregada e com sinal**: tenha um chip de outra operadora de reserva.',
          '**Maquininha integrada ao sistema**: o valor vai do pedido para o terminal, sem digitar, evitando erro de cobrança.',
          '**Defina um limite**: em pedidos muito altos ou clientes novos, prefira pagamento online.',
        ],
      },
      { type: 'h2', text: 'Pagamento online: cuidados que fazem diferença' },
      {
        type: 'ul',
        items: [
          '**PIX confirmado pelo banco, não por comprovante**: print de comprovante pode ser falso. Veja [como evitar o golpe do comprovante](/blog/pix-no-delivery-golpe-do-comprovante).',
          '**Cartão com pré-autorização**: em alguns gateways, o valor fica reservado e só é cobrado quando o pedido é aceito. Se a loja recusar, a reserva é liberada sem estorno.',
          '**Estorno automático** quando um pedido pago é cancelado, nos provedores que oferecem.',
          '**Dinheiro direto na sua conta**, sem intermediário segurando o repasse.',
        ],
      },
      { type: 'h2', text: 'Como decidir o que oferecer' },
      {
        type: 'ol',
        items: [
          'Olhe o histórico: quantos pedidos por forma de pagamento e quantos problemas (troco, calote, cancelamento) em cada uma.',
          'Mantenha dinheiro e maquininha na entrega para quem prefere, mas **destaque o PIX** no checkout.',
          'Use o PIX para **pedidos agendados e encomendas grandes**: o valor fica garantido antes de comprar os insumos.',
          'Se o PIX for liberado só depois do aceite, o cliente paga quando você confirma que tem o pedido; se for antes, você só prepara o que já está pago. Escolha o modo que combina com a sua operação.',
        ],
      },
      { type: 'h2', text: 'Como funciona no Vende Aí' },
      {
        type: 'p',
        text: 'No Vende Aí, você conecta o seu banco ou gateway: o PIX é gerado com QR Code e copia-e-cola no pedido e **confirmado consultando o próprio banco**. O cartão online funciona com vários gateways, com pré-autorização nos que oferecem. Na entrega, dá para receber em dinheiro (com troco informado no pedido) ou na maquininha integrada. O dinheiro vai direto para a sua conta e o sistema **não cobra comissão por pedido**.',
      },
      {
        type: 'cta',
        text: 'Receba PIX e cartão direto na sua conta com o Vende Aí. Teste grátis por 30 dias.',
      },
    ],
    faq: [
      {
        question: 'Vale a pena aceitar só pagamento online no delivery?',
        answer:
          'Para a maioria dos negócios, não: parte dos clientes ainda prefere pagar na entrega. O melhor costuma ser oferecer os dois e incentivar o online, principalmente em pedidos agendados, encomendas e clientes novos.',
      },
      {
        question: 'Como evitar o golpe do comprovante de PIX?',
        answer:
          'Não aceite print como prova. Use um sistema que confirme o PIX consultando o banco ou o gateway; o pedido só é baixado quando o dinheiro realmente entrou.',
      },
      {
        question: 'O que é pré-autorização no cartão?',
        answer:
          'É a reserva do valor no cartão do cliente sem cobrar de imediato. A cobrança só acontece quando o pedido é aceito; se for recusado, a reserva é cancelada e nada é cobrado.',
      },
    ],
  },
];
